// request_app_secret: the secure field for an App's API key or token. The value typed here goes from
// this input straight to the App's secret route (PUT, session-bound, same origin) and is kept only in
// this component's state until it is saved or the card unmounts. It is never put into the composer,
// a message, storage, the address, a log or the transcript; the tool's output names the secret only.
import { useEffect, useId, useState, type FormEvent } from 'react';
import { KeyRoundIcon } from 'lucide-react';
import { companyRequest, CompanyRequestError } from '@/company/api';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import appSecretsCopy from '@/i18n/ns/app-secrets';
import { useSessionBoundFetch } from '@/lib/use-session-guard';
import { WorkspaceIcon } from './icons';
import { useOptionalWorkspace } from './context';
import { noteAppSecretSaved } from '@/components/assistant-ui/app-build-decisions';

export type AppSecretRequest = {
  id: string;
  title: string;
  name: string;
  host: string;
  purpose: string;
  available: boolean;
  alreadySet: boolean;
};

const ID = /^[A-Za-z0-9_-]{1,200}$/u;
const NAME = /^[A-Za-z][A-Za-z0-9_]{0,63}$/u;

export function isAppSecretRequest(value: unknown): value is AppSecretRequest {
  const request = value as AppSecretRequest;
  return (
    !!request &&
    typeof request === 'object' &&
    typeof request.id === 'string' &&
    ID.test(request.id) &&
    typeof request.name === 'string' &&
    NAME.test(request.name) &&
    typeof request.title === 'string' &&
    typeof request.host === 'string' &&
    request.host.length > 0 &&
    typeof request.purpose === 'string' &&
    typeof request.available === 'boolean' &&
    typeof request.alreadySet === 'boolean'
  );
}

type Status = 'idle' | 'saving' | 'saved' | 'owner_only' | 'unavailable' | 'failed';

export function AppSecretCard({ request }: { request: AppSecretRequest }) {
  const copy = useNamespaceCopy(appSecretsCopy);
  const send = useSessionBoundFetch();
  const threadId = useOptionalWorkspace()?.threadId ?? null;
  const inputId = useId();
  const headingId = useId();
  const [value, setValue] = useState('');
  const [status, setStatus] = useState<Status>(request.available ? 'idle' : 'unavailable');
  const [isSet, setIsSet] = useState(request.alreadySet);
  // Nothing outlives the card: the typed value is dropped when it unmounts.
  useEffect(() => () => setValue(''), []);
  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (!value || status === 'saving') return;
    const body = { host: request.host, value };
    setStatus('saving');
    try {
      await companyRequest(
        `/api/v1/company/apps/${encodeURIComponent(request.id)}/secrets/${encodeURIComponent(request.name)}`,
        'PUT',
        body,
        send,
      );
      setValue('');
      setIsSet(true);
      setStatus('saved');
      if (threadId) noteAppSecretSaved(threadId, request.id, request.name);
    } catch (error) {
      const code = error instanceof CompanyRequestError ? error.code : null;
      if (code === 'app_secret_owner_only' || code === 'app_secrets_unavailable') {
        // Nothing the person can retry here: the value is dropped too.
        setValue('');
        setStatus(code === 'app_secret_owner_only' ? 'owner_only' : 'unavailable');
      } else setStatus('failed');
    }
  };
  const canEnter = request.available && status !== 'unavailable' && status !== 'owner_only';
  const notice =
    status === 'saved'
      ? copy.saved
      : status === 'owner_only'
        ? copy.ownerOnly
        : status === 'unavailable'
          ? copy.unavailable
          : status === 'failed'
            ? copy.failed
            : null;
  const title = formatCopy(copy.title, { app: request.title });
  // The service named inside the sentence, wherever the language puts it.
  const [sentBefore, sentAfter = ''] = copy.sentTo.split('{host}');
  return (
    <section
      className="ws-chat-connections ws-app-secret-card"
      aria-labelledby={headingId}
      data-testid="app-secret-card"
    >
      <div className="ws-chat-connections-heading">
        <strong id={headingId}>{title}</strong>
        {isSet && (
          <span className="ws-app-secret-set" data-testid="app-secret-set">
            <WorkspaceIcon name="check" size={12} />
            {copy.alreadySet}
          </span>
        )}
      </div>
      {/* The secret by the name the App's code uses, what it is for and the one service it is sent to. */}
      <div className="ws-chat-connect-row ws-app-secret-key">
        <span className="ws-chat-connect-logo ws-app-secret-mark" aria-hidden="true">
          <KeyRoundIcon size={16} strokeWidth={1.75} />
        </span>
        <div className="ws-chat-connect-copy">
          <strong>
            <code data-testid="app-secret-name">{request.name}</code>
          </strong>
          {request.purpose && <small data-testid="app-secret-purpose">{request.purpose}</small>}
          <small>
            {sentBefore}
            <span data-testid="app-secret-host">{request.host}</span>
            {sentAfter}
          </small>
        </div>
      </div>
      {canEnter && (
        <form className="ws-app-secret-form" onSubmit={(event) => void save(event)}>
          <label htmlFor={inputId} className="ws-sr-only">
            {formatCopy(copy.valueLabel, { name: request.name })}
          </label>
          <div className="ws-app-secret-field">
            <input
              id={inputId}
              type="password"
              name={`app-secret-${request.name}`}
              placeholder={copy.placeholder}
              autoComplete="off"
              spellCheck={false}
              autoCapitalize="off"
              autoCorrect="off"
              data-1p-ignore=""
              data-lpignore="true"
              data-testid="app-secret-value"
              value={value}
              onChange={(event) => setValue(event.target.value)}
              onInput={(event) => setValue(event.currentTarget.value)}
            />
            <button
              type="submit"
              className="ws-pipedream-connect"
              data-emphasis="primary"
              data-state={status === 'saving' ? 'busy' : 'connect'}
              data-testid="app-secret-save"
              disabled={!value || status === 'saving'}
            >
              {status === 'saving' ? copy.saving : isSet ? copy.replace : copy.save}
            </button>
          </div>
        </form>
      )}
      {notice && (
        <p
          className="ws-chat-connect-notice ws-app-secret-notice"
          data-tone={status === 'saved' ? 'done' : 'problem'}
          role={status === 'saved' ? 'status' : 'alert'}
          data-testid="app-secret-notice"
        >
          {status === 'saved' && <WorkspaceIcon name="check" size={13} />}
          {notice}
        </p>
      )}
      <p className="ws-chat-connect-foot">{copy.privacy}</p>
    </section>
  );
}
